import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Flag to prevent double execution in React Strict Mode
    let executed = false;

    const verifyEmail = async () => {
      // Prevent double execution
      if (executed) return;
      executed = true;

      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Token de vérification manquant');
        return;
      }

      try {
        const response = await api.post('/auth/verify-email', {
          token,
        });

        if (response.data.success) {
          setStatus('success');
          setMessage(response.data.message || 'Votre email a été vérifié avec succès !');
        }
      } catch (err: any) {
        setStatus('error');
        const errorMessage =
          err.response?.data?.error ||
          err.message ||
          'Erreur lors de la vérification de l\'email';
        setMessage(errorMessage);
      }
    };

    verifyEmail();
  }, [searchParams, navigate, setUser]);

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {status === 'loading' && (
            <>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 3,
                }}
              >
                <EmailIcon sx={{ color: 'primary.main', fontSize: 40 }} />
              </Box>

              <Typography variant="h5" gutterBottom>
                Vérification en cours...
              </Typography>

              <CircularProgress sx={{ mt: 3, mb: 2 }} />

              <Typography variant="body2" color="text.secondary" align="center">
                Veuillez patienter pendant que nous vérifions votre adresse email
              </Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'success.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 3,
                }}
              >
                <SuccessIcon sx={{ color: 'success.main', fontSize: 50 }} />
              </Box>

              <Typography variant="h4" gutterBottom color="success.main">
                Email Vérifié !
              </Typography>

              <Alert severity="success" sx={{ width: '100%', mt: 2, mb: 3 }}>
                {message}
              </Alert>

              <Alert severity="info" sx={{ width: '100%', mb: 3 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  En attente de validation administrateur
                </Typography>
                <Typography variant="body2">
                  Votre compte est en attente d'approbation par un administrateur.
                  Vous recevrez un email lorsque votre compte sera validé.
                </Typography>
              </Alert>

              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Vous ne pouvez pas encore vous connecter. Veuillez attendre la validation de votre compte.
              </Typography>

              <Button
                variant="contained"
                component={RouterLink}
                to="/login"
                sx={{ mt: 2 }}
              >
                Retour à la page de connexion
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'error.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 3,
                }}
              >
                <ErrorIcon sx={{ color: 'error.main', fontSize: 50 }} />
              </Box>

              <Typography variant="h5" gutterBottom color="error">
                Erreur de Vérification
              </Typography>

              <Alert severity="error" sx={{ width: '100%', mt: 2, mb: 3 }}>
                {message}
              </Alert>

              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Le lien de vérification est peut-être expiré ou invalide.
                Vous pouvez demander un nouveau lien de vérification.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', width: '100%' }}>
                <Button
                  variant="contained"
                  component={RouterLink}
                  to="/resend-verification"
                  fullWidth
                >
                  Renvoyer l'Email de Vérification
                </Button>

                <Button
                  variant="outlined"
                  component={RouterLink}
                  to="/login"
                  fullWidth
                >
                  Retour à la Connexion
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default VerifyEmail;
